import React, { useEffect, useRef, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

import NeighborsScanTab from './NeighborsScanTab';
import NeighborsReportsList from './NeighborsReportsList';
import RecentNeighborScans from './RecentNeighborScans';
import { registerNeighborScanTarget } from '../utils/neighborScanTracking';

// NeighborsTabContainer hosts the three sub-views of the top-level
// Neighbours tab:
//
//   * "All Reports" — table of every connected jnet2 Node and the
//     timestamp of its most recent neighbour-scan report (clicking a
//     row opens the per-device Neighbours Scan Report page).
//   * "Recent Scans" — the last multi-device scans users started, with
//     their status (click one for its tracker).
//   * "Run Scan" — the existing scan-trigger workflow.
const NeighborsTabContainer = ({ devices, allowNeighborsScan = true }) => {
  const [tab, setTab] = useState(0);
  const [highlightScanId, setHighlightScanId] = useState(null);
  const rootRef = useRef(null);
  // A scan started while this tab is shown goes to Recent Scans. Hidden
  // (another top-level tab keeps this one mounted) it stays out of the
  // way and the tracker dialog shows the scan instead.
  useEffect(() => {
    if (!allowNeighborsScan) return undefined;
    return registerNeighborScanTarget((scanId) => {
      const el = rootRef.current;
      const visible = Boolean(el) && (el.offsetParent !== null || el.getClientRects().length > 0);
      if (!visible) return false;
      setHighlightScanId(scanId);
      setTab(1);
      return true;
    });
  }, [allowNeighborsScan]);
  return (
    <Box ref={rootRef}>
      {allowNeighborsScan && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="Neighbours sub-tabs">
            <Tab label="All Reports" id="neighbors-subtab-0" aria-controls="neighbors-subpanel-0" />
            <Tab label="Recent Scans" id="neighbors-subtab-1" aria-controls="neighbors-subpanel-1" />
            <Tab label="Run Scan" id="neighbors-subtab-2" aria-controls="neighbors-subpanel-2" />
          </Tabs>
        </Box>
      )}
      <Box role="tabpanel" hidden={allowNeighborsScan && tab !== 0} id="neighbors-subpanel-0" aria-labelledby="neighbors-subtab-0">
        {( !allowNeighborsScan || tab === 0) && (
          <NeighborsReportsList devices={devices} allowNeighborsScan={allowNeighborsScan} />
        )}
      </Box>
      {allowNeighborsScan && (
        <Box role="tabpanel" hidden={tab !== 1} id="neighbors-subpanel-1" aria-labelledby="neighbors-subtab-1">
          {tab === 1 && <RecentNeighborScans highlightScanId={highlightScanId} />}
        </Box>
      )}
      {allowNeighborsScan && (
        <Box role="tabpanel" hidden={tab !== 2} id="neighbors-subpanel-2" aria-labelledby="neighbors-subtab-2">
          {tab === 2 && <NeighborsScanTab devices={devices} />}
        </Box>
      )}
    </Box>
  );
};

export default NeighborsTabContainer;
